import moment from 'moment'

import clientSDK from '../../../App/ClientSDK'
import { TCalendarDateTemplateInfo } from '../../../App/Util/PrimeCalendarUtil/DateTemplate'
import {
  IAbsenceFormBase,
  IAPIAbsenceFormBase,
  IAPIAbsenceFormBaseResponse,
} from '../../layout/AbsenceFormBase/IAbsenceFormBase'

interface ICreateGeneralAbsence {
  starts_at: string
  ends_at: string
  absence_type_uuid: string
  reason: string
}

interface IDeleteGeneralAbsenceSuccess {
  status: 'success'
}

const baseUrl = 'hr/general-absences'

const generalAbsenceApiTransformer = (
  data: IAPIAbsenceFormBase,
): IAbsenceFormBase => ({
  uuid: data.uuid,
  startsAt: moment(data.starts_at),
  endsAt: moment(data.ends_at),
  absenceType: {
    uuid: data.absence_type.uuid,
    key: data.absence_type.key,
    color: data.absence_type.color,
    shouldBeCaughtUp: data.absence_type.should_be_caught_up,
  },
  reason: data.reason,
})

const generalAbsenceForCalendarTransformer = (data: IAPIAbsenceFormBase): TCalendarDateTemplateInfo => ({
  tooltip: `absence.${data.absence_type.key}`,
  startsAt: moment(data.starts_at),
  endsAt: moment(data.ends_at),
  color: data.absence_type.color,
  isGeneralAbsence: true,
  uuid: data.uuid,
})

const getList = async () => {
  const response = await clientSDK
    .instance()
    .get<IAPIAbsenceFormBaseResponse>(baseUrl)
  const data = response.data
  return data.list.map(absence => generalAbsenceApiTransformer(absence))
}

const getCalendarData = async (startsAt: string, endsAt:string): Promise<TCalendarDateTemplateInfo[]> => {
  const response = await clientSDK
    .instance()
    .get<IAPIAbsenceFormBaseResponse>(
      baseUrl, 
      {starts_at: startsAt, ends_at: endsAt})
  const data = response.data
  return data.list.map(absence => generalAbsenceForCalendarTransformer(absence))
}

const getGeneralAbsence = async (absenceUuid: string) => {
  const response = await clientSDK
    .instance()
    .get<{ data: IAPIAbsenceFormBase }>(`${baseUrl}/${absenceUuid}`)
  const data = response.data.data
  return generalAbsenceApiTransformer(data)
}

const createGeneralAbsence = async (generalAbsence: ICreateGeneralAbsence) => {
  return clientSDK
    .instance()
    .post<ICreateGeneralAbsence>(baseUrl, generalAbsence)
}

const deleteGeneralAbsence = async (absenceUuid: string) => {
  return clientSDK
    .instance()
    .delete<IDeleteGeneralAbsenceSuccess>(`${baseUrl}/${absenceUuid}`)
}

const updateGeneralAbsence = async (generalAbsenceObject: {
  generalAbsence: ICreateGeneralAbsence
  generalAbsenceUuid: string
}) => {
  const { generalAbsence, generalAbsenceUuid } = generalAbsenceObject
  const response = await clientSDK
    .instance()
    .put<ICreateGeneralAbsence>(
      `${baseUrl}/${generalAbsenceUuid}`,
      generalAbsence,
    )
  return response.data
}

const useGeneralAbsencesService = {
  getList,
  getGeneralAbsence,
  createGeneralAbsence,
  deleteGeneralAbsence,
  updateGeneralAbsence,
  getCalendarData,
}

export default useGeneralAbsencesService
